import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { ArrowDown, ArrowUp, Button, Checkbox, ContextMenu, Item, MenuButton } from 'basic-components';
import { Query, ReduxService } from 'dashboard-services';
import { globalActions } from 'primary-components';

import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';

import PatternItem from './PatternItem';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import './BasketHeader.scss';

const useAnimatedCounter = selectedItemsLength => {
  const [isAnimationActive, setIsAnimationActive] = useState(false),
        [symbolsCount, setSymbolsCount] = useState(0),
        [nextSymbolsCount, setNextSymbolsCount] = useState(),
        symbolsCountAnimation = useRef(),
        turnOffAnimation = useRef()

  useEffect(() => {
    setNextSymbolsCount(selectedItemsLength)
    setIsAnimationActive(true)
    clearTimeout(symbolsCountAnimation.current)
    symbolsCountAnimation.current = setTimeout(() => setSymbolsCount(selectedItemsLength), 250)
    clearTimeout(turnOffAnimation.current)
    turnOffAnimation.current = setTimeout(() => setIsAnimationActive(false), 500)
  }, [selectedItemsLength])

  useEffect(() => () => {
    clearTimeout(symbolsCountAnimation.current)
    clearTimeout(symbolsCountAnimation.current)
  }, [])

  return { symbolsCount, nextSymbolsCount, isAnimationActive }
}

const emptyArray = [],
      emptyFun = () => {},
      MAX = 20
const BasketHeader = ({ addToSheet, counterLabel, onClearSelected, onConfigure, onReset, totalSize, symbols, selectedItems, isStartingResults, selectedColumns, withPattern, withSelect, onChangeColumns }) => {
  const dispatch = useDispatch(),
        source = useSelector(state => state.globalState.source),
        selectedItemsLength = selectedItems.length,
        areItemsSelected = useMemo(() => selectedItemsLength > 0, [selectedItemsLength]),
        [isMenuOpen, setIsMenuOpen] = useState(false),
        onMenuChange = useCallback(({ open }) => setIsMenuOpen(open), []),
        onResetLocal = useCallback(() => {
          dispatch(globalActions.getToastsStore()).warning("All changes will be discarded. Do you want to proceed?", "Clear changes",{
            withCancel: false,
            confirmText: "Clear",
            confirmAction: () => {
              onReset()
            }
          })
        }, [dispatch, onReset]),
        areAllSelected = useMemo(() => selectedItemsLength > 0 && selectedItemsLength === totalSize, [selectedItemsLength, totalSize]),
        selectAll = useCallback(() => {
          let symbolsToSelect = []
          if(totalSize > MAX) {
            symbolsToSelect = symbols.slice(0, MAX)
          } else {
            symbolsToSelect = symbols
          }
          const symbolsToSelectLength = symbolsToSelect.length
          dispatch(globalActions.getMessageStore()).info(`We added ${(symbolsToSelectLength >= MAX ? `${MAX} first ` : symbolsToSelectLength)} items. ${(symbolsToSelectLength >= MAX ? " You might want to filter down the results." : "")}`)
          symbolsToSelect.forEach(symbol => {
            dispatch(creatorSeriesActions.swapSelectedSymbol(ReduxService.updateObject(symbol, { pattern: "false" })))
          });
        }, [dispatch, symbols, totalSize]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        { data: columns = emptyArray } = dataStorageQueries.useGetColumns({ getQueryBuilder, selectedSymbols: selectedItems, provider: source }),
        { symbolsCount, nextSymbolsCount, isAnimationActive } = useAnimatedCounter(selectedItemsLength)

  useEffect(() => {
    if(selectedItems.length > 0 && selectedColumns.length === 0 && columns[0]?.value) {
      onChangeColumns([columns[0]?.value])
    }
  }, [columns, dispatch, onChangeColumns, selectedColumns, selectedItems])

  return (
    <div className={classNames(
      "ng-office-app__authed__content__basket-header",
      { "ng-office-app__authed__content__basket-header--is-selected": areItemsSelected }
    )}>
      <div className="ng-office-app__authed__content__basket-header__left">
        <ContextMenu
            itemsSize="unset"
            menuButton={(
              <MenuButton>
                <div className={classNames(
                  "ng-office-app__authed__content__basket-header__left__menu",
                  { "ng-office-app__authed__content__basket-header__left__menu--is-selected": areItemsSelected }
                )}>
                  <Checkbox
                      checked={areAllSelected}
                      indeterminate={areItemsSelected}
                      onChange={emptyFun}
                      tabIndex={-1}
                  >
                    {""}
                  </Checkbox>
                <div className="ng-office-app__authed__content__basket-header__left__menu__counter">
                  <div className={classNames(
                    "ng-office-app__authed__content__basket-header__left__menu__counter__carousel",
                    { "ng-office-app__authed__content__basket-header__left__menu__counter__carousel--is-animation-active": isAnimationActive }
                  )}>
                    <div className="ng-office-app__authed__content__basket-header__left__menu__counter__carousel__value ng-office-app__authed__content__basket-header__left__menu__counter__carousel__value--is-new">
                      {nextSymbolsCount}
                    </div>
                    <div className="ng-office-app__authed__content__basket-header__left__menu__counter__carousel__value ng-office-app__authed__content__basket-header__left__menu__counter__carousel__value--is-pernament">
                      {symbolsCount}
                    </div>
                  </div>
                  <div className="ng-office-app__authed__content__basket-header__left__menu__counter__label">
                    {counterLabel}
                  </div>
                  <div className="ng-office-app__authed__content__basket-header__left__menu__counter__arrow">
                    <If condition={isMenuOpen}>
                      <Then>
                        <ArrowUp
                            color={areItemsSelected ? "white-base" : "grey-dark"}
                            height={12}
                            isWithoutBorder
                            width={12}
                        />
                      </Then>
                      <Else>
                        <ArrowDown
                            color={areItemsSelected ? "white-base" : "grey-dark"}
                            height={12}
                            isWithoutBorder
                            width={12}
                        />
                      </Else>
                    </If>
                  </div>
                </div>
                </div>
              </MenuButton>
            )}
            offsetX={8}
            offsetY={8}
            onMenuChange={onMenuChange}
        >
          <When condition={withSelect}>
            {() => (
              <Item 
                  disabled={selectedItemsLength >= MAX || isStartingResults}
                  onClick={selectAll}
              >
                Select all
              </Item>
            )}
          </When>
          <Item 
              disabled={selectedItemsLength === 0}
              onClick={onClearSelected}
          >
            Clear selected
          </Item>
          <When condition={withPattern}>
            {() => (
              <PatternItem
                  isStartingResults={isStartingResults}
                  selectedItems={selectedItems}
              />
            )}
          </When>
          <Item onClick={onResetLocal}>
            Clear and reset
          </Item>
        </ContextMenu>
      </div>
      <div className="ng-office-app__authed__content__basket-header__right">
        <Button
            disabled={!areItemsSelected}
            onClick={onConfigure}
            size="small"
            variant="secondary"
        >
          Configure
        </Button>
        <Button
            disabled={!areItemsSelected}
            onClick={addToSheet}
            size="small"
            variant="primary"
        >
          Add to sheet
        </Button>
      </div>
    </div>
  )
}

BasketHeader.defaultProps = {
  counterLabel: "selected",
  isStartingResults: false,
  symbols: [],
  totalSize: 0,
  withPattern: true,
  withSelect: true,
}

BasketHeader.propTypes = {
  addToSheet: PropTypes.func.isRequired,
  counterLabel: PropTypes.string,
  isStartingResults: PropTypes.bool,
  onChangeColumns: PropTypes.func.isRequired,
  onClearSelected: PropTypes.func.isRequired,
  onConfigure: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  selectedColumns: PropTypes.array.isRequired,
  selectedItems: PropTypes.array.isRequired,
  symbols: PropTypes.array,
  totalSize: PropTypes.number,
  withPattern: PropTypes.bool,
  withSelect: PropTypes.bool,
}

export default BasketHeader;